import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { CreditApplication, LoanAmountInput, LoanAmountInputSchema } from '../../data/schemas/PrequalificationSchema'
import { dynamicObject } from '../InitialFinancingInformationContainer/InitialFinancingInformationContainer'
import { getValuesFromObject, validateObjectValue } from '../utils/utils'

type Props = {
  getFooter: (handleBack: () => void) => ReactElement
  handleBack: (data: dynamicObject) => void
  stepProgression: ReactElement
  creditApplication: CreditApplication
  onFormSubmitted: (data: dynamicObject) => void
}

function LoanAmountForm({ getFooter: footer, handleBack, creditApplication, stepProgression, onFormSubmitted }: Props) {
  const { t } = useTranslation()

  const {
    handleSubmit,
    register,
    reset,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<LoanAmountInput>({
    mode: 'onBlur',
    resolver: yupResolver(LoanAmountInputSchema),
  })

  React.useEffect(() => {
    const defaultValues = getValuesFromObject(Object.keys(LoanAmountInputSchema.getDefault()), creditApplication)
    reset(defaultValues)
    validateObjectValue(getValues(), trigger)
  }, [creditApplication])

  const goBack = () => {
    handleBack(getValues())
  }

  const renderErrorMessage = () => {
    if (errors.requestedLoanAmount?.type !== 'required') {
      return t<string>(errors.requestedLoanAmount?.message ?? '')
    }
    return t('common.errors.emptyLoanAmount')
  }

  return (
    <main className="form-section step-1-active">
      {stepProgression}
      <section className="step-content">
        <h1 className="h3">{t('loanAmount.desiredAmount')}</h1>
        <div className="paragraph">
          <p>{t('loanAmount.amountThresholds')}</p>
        </div>

        <form onSubmit={handleSubmit(onFormSubmitted)}>
          <div className={`control-group ${errors.requestedLoanAmount && 'error'}`}>
            <label htmlFor="requestedLoanAmount">{t('loanAmount.amount')}</label>
            <div className="number-wrap">
              <input id="requestedLoanAmount" type="text" {...register('requestedLoanAmount')} />
            </div>
            {errors.requestedLoanAmount && <p className="error-message">{renderErrorMessage()}</p>}
          </div>
          {footer(goBack)}
        </form>
      </section>
    </main>
  )
}

export default LoanAmountForm
